import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, Clock, Plane, Route as RouteIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { addRecent, getRecent, RecentItem } from "@/lib/recent";
import type { AirportSearchResult } from "@/lib/flightApi";
import { parseQuery, navigateForQuery } from "@/lib/parseQuery";
import { useSEO } from "@/lib/seo";

import { AffiliateTile } from "@/components/AffiliateTile";
import { useRandomAffiliate } from "@/hooks/useRandomAffiliate";
import { cn } from "@/lib/utils";

const POPULAR = [
  { code: "ARN", city: "Stockholm" },
  { code: "GOT", city: "Göteborg" },
  { code: "MMX", city: "Malmö" },
  { code: "CPH", city: "Köpenhamn" },
  { code: "OSL", city: "Oslo" },
  { code: "LHR", city: "London" },
  { code: "CDG", city: "Paris" },
  { code: "JFK", city: "New York" },
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [suggestions, setSuggestions] = useState<AirportSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const reqId = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setRecent(getRecent()), []);
  // Desktop-only autofocus to avoid forced reflow + mobile keyboard pop on load
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(min-width: 768px)").matches) {
      inputRef.current?.focus();
    }
  }, []);
  const ad = useRandomAffiliate("home");

  useSEO({
    title: "Flyg.info — Spåra flyg, flygplatser & rutter i realtid",
    description: "Sök på flightnummer, rutt eller flygplats. Live-position, status, ankomster, avgångar och historik.",
    canonical: "https://flyg.info/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Flyg.info",
      url: "https://flyg.info/",
      applicationCategory: "TravelApplication",
      operatingSystem: "Web",
      inLanguage: "sv-SE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "SEK" },
    },
  });

  useEffect(() => {
    const text = q.trim();
    const parsed = parseQuery(text);
    if (parsed.kind !== "airport-search" || text.length < 2) {
      setSuggestions([]);
      return;
    }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const { searchAirports } = await import("@/lib/flightApi");
        const r = await searchAirports(text);
        if (id === reqId.current) setSuggestions(r.slice(0, 5));
      } catch {
        if (id === reqId.current) setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  function submit(e?: FormEvent) {
    e?.preventDefault();
    const parsed = parseQuery(q);
    const url = navigateForQuery(parsed);
    if (url) {
      if (parsed.kind === "flight") addRecent({ type: "flight", value: parsed.value });
      if (parsed.kind === "route") addRecent({ type: "route", from: parsed.from, to: parsed.to });
      navigate(url);
      return;
    }
    if (suggestions[0]) {
      const a = suggestions[0];
      navigate(`/airport/${(a.iata || a.icao).toUpperCase()}`);
    }
  }

  const parsed = parseQuery(q);
  const hint =
    parsed.kind === "flight" ? "Flightnummer" :
    parsed.kind === "route" ? "Rutt" :
    parsed.kind === "airport" ? "Flygplats (kod)" :
    parsed.kind === "airport-search" ? "Flygplats" : "";

  return (
    <div className="bg-hero">
      <section className="px-4 sm:px-6 md:px-10 pt-12 md:pt-24 pb-10 md:pb-16 max-w-4xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
          Flygradar · realtid · sv-SE
        </div>
        <h1 className="font-display text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] font-medium tracking-tight leading-[1.02]">
          Spåra valfritt flyg
          <br />
          <span className="italic text-muted-foreground">i realtid.</span>
        </h1>
        <p className="mt-5 text-sm sm:text-base md:text-lg text-muted-foreground max-w-xl">
          En sökruta. Skriv flightnummer (<span className="font-mono">SK1234</span>),
          rutt (<span className="font-mono">ARN-LHR</span>) eller en flygplats.
        </p>

        <form onSubmit={submit} className="mt-7 relative">
          <div className="flex items-center gap-2 border-b-2 border-foreground/80 pb-2">
            <Search className="h-5 w-5 text-muted-foreground shrink-0 hidden sm:block" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="t.ex. SK1234 · ARN-LHR · Arlanda"
              className="border-0 bg-transparent px-0 h-12 text-base sm:text-lg md:text-2xl font-display tracking-tight focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50 min-w-0"
            />
            {hint && (
              <span className="hidden sm:inline-flex shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground font-mono px-2 py-1 rounded bg-secondary">
                {hint}
              </span>
            )}
            <Button type="submit" size="sm" className="shrink-0 h-10 sm:h-9 px-3" aria-label="Spåra">
              <span className="hidden sm:inline">Spåra</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {open && (parsed.kind === "airport-search" && suggestions.length > 0) && (
            <div className="absolute left-0 right-0 mt-2 bg-card border border-rule rounded-md shadow-soft overflow-hidden z-20">
              {suggestions.map((a) => (
                <button
                  key={a.icao}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => navigate(`/airport/${(a.iata || a.icao).toUpperCase()}`)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-surface-2 transition-colors"
                >
                  <span className="font-mono text-xs w-12 shrink-0">{a.iata || a.icao}</span>
                  <span className="flex-1 min-w-0">
                    <span className="text-sm truncate block">{a.name}</span>
                    <span className="text-xs text-muted-foreground truncate block">
                      {[a.city, a.country].filter(Boolean).join(", ")}
                    </span>
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </form>

        {recent.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              <Clock className="h-3 w-3" /> Senaste
            </div>
            <div className="flex flex-wrap gap-2">
              {recent.slice(0, 6).map((r, i) => (
                <button
                  key={i}
                  onClick={() => navigate(r.type === "flight" ? `/flight/${r.value}` : `/route/${r.from}-${r.to}`)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-rule bg-card hover:border-foreground/30 px-3 py-1 text-xs font-mono transition-colors"
                >
                  {r.type === "flight" ? <Plane className="h-3 w-3" /> : <RouteIcon className="h-3 w-3" />}
                  {r.type === "flight" ? r.value : `${r.from} → ${r.to}`}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="px-4 sm:px-6 md:px-10 pt-2 pb-8 max-w-4xl mx-auto">
        <AffiliateTile affiliate={ad} variant="prominent" />
      </section>

      <section className="px-4 sm:px-6 md:px-10 py-10 md:py-12 max-w-4xl mx-auto border-t border-rule">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">Populära flygplatser</h2>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hidden sm:inline">Live-status</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-rule border border-rule rounded-md overflow-hidden">
          {POPULAR.map((a) => (
            <button
              key={a.code}
              onClick={() => navigate(`/airport/${a.code}`)}
              className={cn(
                "group bg-card hover:bg-surface-2 text-left p-4 transition-colors"
              )}
            >
              <div className="font-mono text-2xl font-semibold tracking-tight">{a.code}</div>
              <div className="text-xs text-muted-foreground mt-1">{a.city}</div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}